/**
 * iter318 wave 13 — VyomOS Legal pages (Privacy & Terms).
 * iter336 — CMS-driven when Super Admin has published "privacy"/"terms"
 * sections; otherwise falls back to the static placeholder copy.
 */
import { Link } from 'react-router-dom';
import { usePageSeo } from './usePageSeo';
import { useWebsiteContent } from '../../../hooks/useWebsiteContent';
import SectionRenderer from './SectionRenderer';

const PRIVACY = {
  title: 'Privacy Policy',
  updated: 'February 2026',
  sections: [
    ['Overview', 'VyomOS provides an operations platform for academies and facilities. This policy explains what information we collect, how we use it, and the choices you have.'],
    ['Information we collect', 'Account information (name, email, organization), operational data your organization enters into VyomOS (participants, schedules, payments), and technical data (browser, device, IP address) required to operate the service.'],
    ['How we use information', 'To operate and secure the platform, deliver features you enable, communicate about service updates, prevent fraud, and comply with legal obligations.'],
    ['Sharing', 'We do not sell personal information. We share limited data only with sub-processors (hosting, payments, email delivery) under strict data-protection agreements.'],
    ['Data retention', 'Operational data is retained for the life of your subscription plus 90 days. You can request export or deletion at any time.'],
    ['Your rights', 'Depending on your jurisdiction, you may have rights to access, correct, delete, or port your personal information. Contact privacy@vyomos.io to exercise them.'],
    ['Security', 'Encryption in transit and at rest, role-based access controls, audit logging, and least-privilege operational access. Full details available on request.'],
    ['Contact', 'Questions? Email privacy@vyomos.io.'],
  ],
};

const TERMS = {
  title: 'Terms of Service',
  updated: 'February 2026',
  sections: [
    ['Agreement', 'By accessing or using VyomOS, you agree to these terms on behalf of yourself and your organization.'],
    ['Service description', 'VyomOS provides a hosted software platform for managing academy and facility operations. Features may evolve; we may release new capabilities or retire redundant ones with reasonable notice.'],
    ['Accounts', 'You are responsible for maintaining accurate account information, keeping credentials confidential, and controlling access for users you invite into your tenant workspace.'],
    ['Acceptable use', 'You may not use VyomOS to violate laws, infringe rights, transmit malware, or interfere with the platform&apos;s security or availability.'],
    ['Payments', 'Subscription fees are billed in advance per plan. Payment-processor terms (e.g., Stripe) also apply to funds you collect through VyomOS.'],
    ['Data ownership', 'You own your data. We are a data processor operating under the privacy policy and applicable regulations.'],
    ['Suspension & termination', 'We may suspend the service for security or non-payment reasons. You may cancel any time from Super Admin. Data export is available for 30 days after cancellation.'],
    ['Warranties & liability', 'The service is provided on an "as is" basis. To the maximum extent permitted by law, our liability is limited to fees paid in the prior 12 months.'],
    ['Changes', 'We may update these terms and will notify tenants of material changes at least 30 days in advance.'],
    ['Contact', 'Questions? Email legal@vyomos.io.'],
  ],
};

export default function VyomosLegal({ kind = 'privacy' }) {
  usePageSeo(kind);
  const page = kind === 'terms' ? 'terms' : 'privacy';
  const { content, loading } = useWebsiteContent(page);
  const cmsSections = content?.sections || [];
  if (loading && !content) {
    return <div className="min-h-[60vh]" data-testid={`vyomos-${page}-loading`} />;
  }
  if (cmsSections.length > 0) {
    return (
      <div data-testid={`vyomos-${page}-cms`}>
        {cmsSections.map((s) => <SectionRenderer key={s.id} section={s} />)}
      </div>
    );
  }
  return <StaticLegal kind={kind} />;
}

function StaticLegal({ kind = 'privacy' }) {
  const doc = kind === 'terms' ? TERMS : PRIVACY;
  const testId = kind === 'terms' ? 'vyomos-terms' : 'vyomos-privacy';

  return (
    <div data-testid={testId}>
      <section className="bg-gradient-to-b from-[#EAF5FF] to-[#F8FBFF]">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="text-xs uppercase tracking-[0.15em] font-semibold text-[#1677FF] mb-3">Legal</div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-2">{doc.title}</h1>
          <p className="text-sm text-[#566579]">Last updated: {doc.updated}</p>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 py-16 space-y-10">
          {doc.sections.map(([title, body]) => (
            <div key={title}>
              <h2 className="text-xl font-bold mb-3 text-[#10233F]">{title}</h2>
              <p className="text-[#566579] leading-relaxed">{body}</p>
            </div>
          ))}
          <div className="pt-4 border-t border-[#DCE6F0] flex gap-4 text-sm">
            <Link to={kind === 'terms' ? '/privacy' : '/terms'} className="text-[#1677FF] hover:underline">
              {kind === 'terms' ? 'Privacy Policy' : 'Terms of Service'}
            </Link>
            <Link to="/contactus" className="text-[#566579] hover:text-[#10233F]">Contact us</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
